import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  SphereGeometry
} from 'three';

/** Super vs melee/cannon silhouette lock (FID-MIN-03). */
export const SUPER_MINION_READABILITY = Object.freeze({
  torsoTopRadius: 0.7,
  torsoBotRadius: 0.5,
  torsoHeight: 1.55,
  helmetRadius: 0.45,
  pauldronW: 0.5,
  pauldronH: 0.4,
  pauldronD: 0.6,
  pauldronEmissive: 0.7
});

/** Cannon vs super silhouette lock (FID-MIN-09 / SPY-2003). */
export const CANNON_MINION_READABILITY = Object.freeze({
  chassisW: 1.0,
  chassisH: 0.5,
  chassisD: 1.2,
  barrelLength: 1.48,
  barrelTipRadius: 0.22,
  barrelBaseRadius: 0.3
});

export class MinionMeshFactory {
  /**
   * Creates a 3D mesh representation for a Melee Minion (Shield + Sword + Helmet).
   */
  static createMeleeMinionMesh(team = 'blue') {
    const group = new Group();
    const color = team === 'blue' ? 0x3b82f6 : 0xef4444;
    const accentColor = team === 'blue' ? 0x1d4ed8 : 0xb91c1c;

    const bodyMat = new MeshStandardMaterial({ color, roughness: 0.4 });
    const armorMat = new MeshStandardMaterial({ color: 0x64748b, metalness: 0.6, roughness: 0.3 });
    const shieldMat = new MeshStandardMaterial({ color: accentColor, metalness: 0.4 });

    // Stocky Body
    const body = new Mesh(new CylinderGeometry(0.45, 0.4, 1.1, 10), bodyMat);
    body.position.y = 0.55;
    group.add(body);

    // Helmet / Head
    const helmet = new Mesh(new SphereGeometry(0.35, 12, 12), armorMat);
    helmet.position.y = 1.25;
    group.add(helmet);

    // Shield (on left side)
    const shield = new Mesh(new BoxGeometry(0.12, 0.6, 0.45), shieldMat);
    shield.position.set(-0.45, 0.7, 0.1);
    group.add(shield);

    // Sword (on right side)
    const sword = new Mesh(new BoxGeometry(0.08, 0.7, 0.1), armorMat);
    sword.position.set(0.45, 0.7, 0.2);
    sword.rotation.z = -Math.PI / 8;
    group.add(sword);

    return group;
  }

  /**
   * Creates a 3D mesh representation for a Ranged Minion (Robe + Wizard Hat + Staff).
   */
  static createRangedMinionMesh(team = 'blue') {
    const group = new Group();
    const color = team === 'blue' ? 0x60a5fa : 0xf87171;
    const orbColor = team === 'blue' ? 0x38bdf8 : 0xfca5a5;

    const robeMat = new MeshStandardMaterial({ color, roughness: 0.6 });
    const woodMat = new MeshStandardMaterial({ color: 0x78350f, roughness: 0.8 });
    const orbMat = new MeshStandardMaterial({ color: orbColor, emissive: orbColor, emissiveIntensity: 0.5 });

    // Robed Body (Conical base)
    const robe = new Mesh(new CylinderGeometry(0.25, 0.48, 1.0, 10), robeMat);
    robe.position.y = 0.5;
    group.add(robe);

    // Wizard Hat (Cone head)
    const hat = new Mesh(new ConeGeometry(0.38, 0.65, 8), robeMat);
    hat.position.y = 1.25;
    hat.rotation.x = -0.15;
    group.add(hat);

    // Caster Staff (right hand)
    const staff = new Mesh(new CylinderGeometry(0.04, 0.04, 1.2, 8), woodMat);
    staff.position.set(0.35, 0.6, 0.15);
    group.add(staff);

    // Glowing Magic Orb at tip of staff
    const orb = new Mesh(new SphereGeometry(0.14, 10, 10), orbMat);
    orb.position.set(0.35, 1.25, 0.15);
    group.add(orb);

    return group;
  }

  /**
   * Creates a 3D mesh representation for a Cannon Minion (Wheeled Chassis + Cannon Barrel).
   */
  static createCannonMinionMesh(team = 'blue') {
    const group = new Group();
    group.userData.minionKind = 'cannon';
    const teamColor = team === 'blue' ? 0x2563eb : 0xd97706;
    const r = CANNON_MINION_READABILITY;

    const chassisMat = new MeshStandardMaterial({ color: teamColor, roughness: 0.4 });
    const metalMat = new MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.2 });

    // Carriage / Chassis (wider than pre-lock so silhouette ≠ biped super)
    const chassis = new Mesh(
      new BoxGeometry(r.chassisW, r.chassisH, r.chassisD),
      chassisMat
    );
    chassis.position.y = 0.45;
    chassis.userData.minionPart = 'chassis';
    group.add(chassis);

    // Heavy Cannon Barrel
    const barrel = new Mesh(
      new CylinderGeometry(r.barrelTipRadius, r.barrelBaseRadius, r.barrelLength, 12).rotateX(
        Math.PI / 2
      ),
      metalMat
    );
    barrel.position.set(0, 0.75, 0.2);
    barrel.userData.minionPart = 'barrel';
    group.add(barrel);

    // 4 Wheels
    const wheelMat = new MeshStandardMaterial({ color: 0x1e293b, roughness: 0.5 });
    const wheelGeo = new CylinderGeometry(0.22, 0.22, 0.12, 10).rotateZ(Math.PI / 2);

    const positions = [
      [-0.52, 0.22, 0.4],
      [0.52, 0.22, 0.4],
      [-0.52, 0.22, -0.4],
      [0.52, 0.22, -0.4]
    ];

    positions.forEach(([x, y, z]) => {
      const wheel = new Mesh(wheelGeo, wheelMat);
      wheel.position.set(x, y, z);
      wheel.userData.minionPart = 'wheel';
      group.add(wheel);
    });

    return group;
  }

  /**
   * Creates a 3D mesh representation for a Super Minion (Armored Juggernaut + Glowing Pauldrons).
   */
  static createSuperMinionMesh(team = 'blue') {
    const group = new Group();
    group.userData.minionKind = 'super';
    const color = team === 'blue' ? 0x1e40af : 0x991b1b;
    const glowColor = team === 'blue' ? 0x60a5fa : 0xf87171;
    const r = SUPER_MINION_READABILITY;

    const armorMat = new MeshStandardMaterial({ color, metalness: 0.7, roughness: 0.2 });
    const glowMat = new MeshStandardMaterial({
      color: glowColor,
      emissive: glowColor,
      emissiveIntensity: r.pauldronEmissive
    });

    // Massive Armored Torso
    const torso = new Mesh(
      new CylinderGeometry(r.torsoTopRadius, r.torsoBotRadius, r.torsoHeight, 10),
      armorMat
    );
    torso.position.y = r.torsoHeight / 2 + 0.1;
    torso.userData.minionPart = 'torso';
    group.add(torso);

    // Heavy Head Helmet
    const helmet = new Mesh(new SphereGeometry(r.helmetRadius, 12, 12), armorMat);
    helmet.position.y = r.torsoHeight + 0.25;
    helmet.userData.minionPart = 'helmet';
    group.add(helmet);

    // Giant Glowing Pauldrons
    const leftPauldron = new Mesh(
      new BoxGeometry(r.pauldronW, r.pauldronH, r.pauldronD),
      glowMat
    );
    leftPauldron.position.set(-0.75, 1.45, 0);
    leftPauldron.userData.minionPart = 'pauldron';
    group.add(leftPauldron);

    const rightPauldron = new Mesh(
      new BoxGeometry(r.pauldronW, r.pauldronH, r.pauldronD),
      glowMat
    );
    rightPauldron.position.set(0.75, 1.45, 0);
    rightPauldron.userData.minionPart = 'pauldron';
    group.add(rightPauldron);

    return group;
  }
}
